import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Badge,
  Avatar,
  InputAdornment,
  Collapse,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent
} from '@mui/lab';
import {
  AccountBalance as AccountIcon,
  AccountBalanceWallet as WalletIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  RemoveCircle as DeductionIcon,
  CheckCircle as RefundIcon,
  Warning as WarningIcon,
  Timer as PendingIcon,
  CreditCard as CardIcon,
  LocalAtm as CashIcon,
  Description as CheckIcon,
  Assignment as PlanIcon,
  TrendingUp as ChargeIcon,
  TrendingDown as CreditIcon,
  History as HistoryIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Note as NoteIcon,
  Camera as PhotoIcon,
  Gavel as DisputeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Home as SiteIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deposits-tabpanel-${index}`}
      aria-labelledby={`deposits-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DepositsPage: React.FC = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [deposits, setDeposits] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountSummary, setAccountSummary] = useState<any>(null);
  const [depositSummary, setDepositSummary] = useState<any>(null);
  const [selectedDeposit, setSelectedDeposit] = useState<any>(null);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [guests, setGuests] = useState<any[]>([]);
  
  // UI states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [depositFilter, setDepositFilter] = useState({
    type: 'all',
    status: 'all'
  });
  const [accountFilter, setAccountFilter] = useState('with_balance');
  const [expandedDeposit, setExpandedDeposit] = useState<string | null>(null);
  
  // Dialog states
  const [depositDialog, setDepositDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);
  const [deductionDialog, setDeductionDialog] = useState(false);
  const [chargeDialog, setChargeDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [statementDialog, setStatementDialog] = useState(false);
  const [creditTermsDialog, setCreditTermsDialog] = useState(false);
  
  // Form states
  const [depositForm, setDepositForm] = useState({
    guest: null as any,
    type: 'security',
    amount: '',
    payment: {
      method: 'cash',
      reference: '',
      checkDetails: {
        checkNumber: '',
        bankName: ''
      }
    },
    conditions: {
      refundable: true,
      refundPolicy: 'after_inspection',
      holdDays: 0
    }
  });
  
  const [refundForm, setRefundForm] = useState({
    amount: '',
    reason: '',
    method: 'original_method'
  });
  
  const [deductionForm, setDeductionForm] = useState({
    amount: '',
    reason: '',
    category: 'damage',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [depositFilter, accountFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDeposits(),
        fetchAccounts(),
        fetchSummaries(),
        fetchGuests()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeposits = async () => {
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (depositFilter.type !== 'all') params.type = depositFilter.type;
      if (depositFilter.status !== 'all') params.status = depositFilter.status;
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/deposits`,
        { 
          params,
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      setDeposits(response.data.deposits);
    } catch (error) {
      console.error('Error fetching deposits:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      let endpoint = '/deposits/accounts/with-balance';
      if (accountFilter === 'overdue') {
        endpoint = '/deposits/accounts/overdue';
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchSummaries = async () => {
    try {
      const [depositRes, accountRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/deposits/reports/summary`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/deposits/accounts/summary`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      
      setDepositSummary(depositRes.data);
      setAccountSummary(accountRes.data);
    } catch (error) {
      console.error('Error fetching summaries:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/guests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const handleCreateDeposit = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/deposits`,
        {
          ...depositForm,
          amount: parseFloat(depositForm.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDepositDialog(false);
      fetchData();
      alert('Deposit created successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error creating deposit');
    }
  };

  const handleRefundDeposit = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/deposits/${selectedDeposit._id}/refund`,
        {
          ...refundForm,
          amount: parseFloat(refundForm.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRefundDialog(false);
      fetchData();
      alert('Refund processed successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error processing refund');
    }
  };

  const handleAddDeduction = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/deposits/${selectedDeposit._id}/deduction`,
        {
          ...deductionForm,
          amount: parseFloat(deductionForm.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDeductionDialog(false);
      fetchData();
      alert('Deduction added successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding deduction');
    }
  };

  const getDepositTypeColor = (type: string) => {
    const colors: any = {
      'security': 'primary',
      'damage': 'warning',
      'pet': 'info',
      'key': 'secondary',
      'advance': 'success',
      'booking': 'success'
    };
    return colors[type] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'held': 'info',
      'partial_refunded': 'warning',
      'refunded': 'success',
      'forfeited': 'error',
      'applied': 'default',
      'expired': 'error'
    };
    return colors[status] || 'default';
  };

  const renderDepositTimeline = (deposit: any) => (
    <Timeline position="alternate">
      <TimelineItem>
        <TimelineSeparator>
          <TimelineDot color="primary">
            <PaymentIcon />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Typography variant="body2">
            Received ${deposit.amount.original.toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(deposit.payment.receivedDate).format('MMM D, YYYY')}
          </Typography>
        </TimelineContent>
      </TimelineItem>
      
      {deposit.deductions.map((deduction: any, index: number) => (
        <TimelineItem key={index}>
          <TimelineSeparator>
            <TimelineDot color="error">
              <DeductionIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2">
              Deduction: ${deduction.amount.toFixed(2)}
            </Typography>
            <Typography variant="caption">
              {deduction.reason}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      ))}
      
      {deposit.refunds.map((refund: any, index: number) => (
        <TimelineItem key={index}>
          <TimelineSeparator>
            <TimelineDot color="success">
              <RefundIcon />
            </TimelineDot>
            {index < deposit.refunds.length - 1 && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2">
              Refunded: ${refund.amount.toFixed(2)}
            </Typography>
            <Typography variant="caption">
              {refund.reason}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );

  const renderMetricCard = (title: string, value: any, subtitle?: string, icon?: React.ReactNode, color?: string) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" color={color}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Deposits & Accounts
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage security deposits and guest account balances
        </Typography>
      </Box>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Total Deposits Held',
            `$${depositSummary?.reduce((sum: number, d: any) => sum + d.totalHeld, 0).toFixed(2) || '0.00'}`,
            'Current balance',
            <WalletIcon color="primary" />
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Active Deposits',
            deposits.filter(d => d.status === 'held').length,
            'Deposits on hold',
            <AccountIcon color="info" />
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Total AR Balance',
            `$${accountSummary?.totalBalance?.toFixed(2) || '0.00'}`,
            'Accounts receivable',
            <MoneyIcon color="warning" />,
            'warning.main'
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Overdue Accounts',
            accountSummary?.accountsWithBalance || 0,
            'Need attention',
            <WarningIcon color="error" />
          )}
        </Grid>
      </Grid>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Deposits" icon={<WalletIcon />} iconPosition="start" />
          <Tab label="Account Balances" icon={<AccountIcon />} iconPosition="start" />
          <Tab label="Payment Plans" icon={<PlanIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Deposits Tab */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={depositFilter.type}
                    onChange={(e) => setDepositFilter({ ...depositFilter, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="security">Security</MenuItem>
                    <MenuItem value="damage">Damage</MenuItem>
                    <MenuItem value="pet">Pet</MenuItem>
                    <MenuItem value="advance">Advance</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={depositFilter.status}
                    onChange={(e) => setDepositFilter({ ...depositFilter, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="held">Held</MenuItem>
                    <MenuItem value="refunded">Refunded</MenuItem>
                    <MenuItem value="applied">Applied</MenuItem>
                    <MenuItem value="forfeited">Forfeited</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              
              <Button
                variant="contained"
                startIcon={<MoneyIcon />}
                onClick={() => setDepositDialog(true)}
              >
                New Deposit
              </Button>
            </Stack>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>ID</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Original</TableCell>
                  <TableCell>Remaining</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deposits.map((deposit) => (
                  <React.Fragment key={deposit._id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedDeposit(
                            expandedDeposit === deposit._id ? null : deposit._id
                          )}
                        >
                          {expandedDeposit === deposit._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{deposit.depositId}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {deposit.guest?.firstName} {deposit.guest?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {deposit.guest?.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={deposit.type}
                          size="small"
                          color={getDepositTypeColor(deposit.type)}
                        />
                      </TableCell>
                      <TableCell>${deposit.amount.original.toFixed(2)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={deposit.amount.remaining > 0 ? 'success.main' : 'text.secondary'}
                        >
                          ${deposit.amount.remaining.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={deposit.status}
                          size="small"
                          color={getStatusColor(deposit.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {dayjs(deposit.payment.receivedDate).format('MM/DD/YYYY')}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {deposit.status === 'held' && deposit.canRefund && (
                            <Tooltip title="Process Refund">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedDeposit(deposit);
                                  setRefundDialog(true);
                                }}
                              >
                                <RefundIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {deposit.status === 'held' && (
                            <Tooltip title="Add Deduction">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedDeposit(deposit);
                                  setDeductionDialog(true);
                                }}
                              >
                                <DeductionIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Add Note">
                            <IconButton size="small">
                              <NoteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0 }}>
                        <Collapse in={expandedDeposit === deposit._id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Transaction History
                                </Typography>
                                {renderDepositTimeline(deposit)}
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Details
                                </Typography>
                                <List dense>
                                  <ListItem>
                                    <ListItemText
                                      primary="Payment Method"
                                      secondary={deposit.payment.method}
                                    />
                                  </ListItem>
                                  {deposit.payment.reference && (
                                    <ListItem>
                                      <ListItemText
                                        primary="Reference"
                                        secondary={deposit.payment.reference}
                                      />
                                    </ListItem>
                                  )}
                                  <ListItem>
                                    <ListItemText
                                      primary="Refund Policy"
                                      secondary={deposit.conditions.refundPolicy}
                                    />
                                  </ListItem>
                                  {deposit.conditions.expirationDate && (
                                    <ListItem>
                                      <ListItemText
                                        primary="Expires"
                                        secondary={dayjs(deposit.conditions.expirationDate).format('MM/DD/YYYY')}
                                      />
                                    </ListItem>
                                  )}
                                </List>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={100} // This should come from API
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Account Balances Tab */}
          <Box sx={{ mb: 3 }}>
            <ToggleButtonGroup
              value={accountFilter}
              exclusive
              onChange={(e, value) => value && setAccountFilter(value)}
              size="small"
            >
              <ToggleButton value="with_balance">
                With Balance
              </ToggleButton>
              <ToggleButton value="overdue">
                Overdue
              </ToggleButton>
              <ToggleButton value="all">
                All Accounts
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={3}>
            {accounts.map((account) => (
              <Grid item xs={12} md={6} lg={4} key={account._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <PersonIcon />
                      </Avatar>
                      <Chip
                        label={account.collections.status}
                        size="small"
                        color={account.collections.status === 'good_standing' ? 'success' : 'warning'}
                      />
                    </Box>
                    
                    <Typography variant="h6" gutterBottom>
                      {account.guest?.firstName} {account.guest?.lastName}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Current Balance
                      </Typography>
                      <Typography variant="h5" color="warning.main">
                        ${account.balance.current.toFixed(2)}
                      </Typography>
                    </Box>
                    
                    {account.creditTerms.enabled && (
                      <LinearProgress
                        variant="determinate"
                        value={(account.balance.current / account.creditTerms.limit) * 100}
                        sx={{ mb: 1 }}
                      />
                    )}
                    
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={`${account.transactions.length} transactions`}
                        size="small"
                        variant="outlined"
                      />
                      {account.aging.days30 > 0 && (
                        <Chip
                          label="30+ days"
                          size="small"
                          color="warning"
                        />
                      )}
                    </Stack>
                  </CardContent>
                  
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<ChargeIcon />}
                      onClick={() => {
                        setSelectedAccount(account);
                        setChargeDialog(true);
                      }}
                    >
                      Charge
                    </Button>
                    <Button
                      size="small"
                      startIcon={<PaymentIcon />}
                      onClick={() => {
                        setSelectedAccount(account);
                        setPaymentDialog(true);
                      }}
                    >
                      Payment
                    </Button>
                    <IconButton size="small">
                      <HistoryIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Payment Plans Tab */}
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PlanIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Payment Plans
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage guest payment plans and installments
            </Typography>
          </Box>
        </TabPanel>
      </Paper>

      {/* New Deposit Dialog */}
      <Dialog open={depositDialog} onClose={() => setDepositDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Deposit</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Autocomplete
              options={guests}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={depositForm.guest}
              onChange={(e, value) => setDepositForm({ ...depositForm, guest: value })}
              renderInput={(params) => (
                <TextField {...params} label="Guest" required />
              )}
            />
            
            <FormControl fullWidth>
              <InputLabel>Deposit Type</InputLabel>
              <Select
                value={depositForm.type}
                onChange={(e) => setDepositForm({ ...depositForm, type: e.target.value })}
                label="Deposit Type"
              >
                <MenuItem value="security">Security Deposit</MenuItem>
                <MenuItem value="damage">Damage Deposit</MenuItem>
                <MenuItem value="pet">Pet Deposit</MenuItem>
                <MenuItem value="key">Key Deposit</MenuItem>
                <MenuItem value="advance">Advance Payment</MenuItem>
                <MenuItem value="booking">Booking Deposit</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Amount"
              type="number"
              value={depositForm.amount}
              onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={depositForm.payment.method}
                onChange={(e) => setDepositForm({ 
                  ...depositForm, 
                  payment: { ...depositForm.payment, method: e.target.value }
                })}
                label="Payment Method"
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit Card</MenuItem>
                <MenuItem value="debit">Debit Card</MenuItem>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="ach">ACH Transfer</MenuItem>
              </Select>
            </FormControl>
            
            {depositForm.payment.method === 'check' && (
              <>
                <TextField
                  label="Check Number"
                  value={depositForm.payment.checkDetails.checkNumber}
                  onChange={(e) => setDepositForm({
                    ...depositForm,
                    payment: {
                      ...depositForm.payment,
                      checkDetails: {
                        ...depositForm.payment.checkDetails,
                        checkNumber: e.target.value
                      }
                    }
                  })}
                  fullWidth
                />
                <TextField
                  label="Bank Name"
                  value={depositForm.payment.checkDetails.bankName}
                  onChange={(e) => setDepositForm({
                    ...depositForm,
                    payment: {
                      ...depositForm.payment,
                      checkDetails: {
                        ...depositForm.payment.checkDetails,
                        bankName: e.target.value
                      }
                    }
                  })}
                  fullWidth
                />
              </>
            )}
            
            <FormControl fullWidth>
              <InputLabel>Refund Policy</InputLabel>
              <Select
                value={depositForm.conditions.refundPolicy}
                onChange={(e) => setDepositForm({
                  ...depositForm,
                  conditions: { ...depositForm.conditions, refundPolicy: e.target.value }
                })}
                label="Refund Policy"
              >
                <MenuItem value="immediate">Immediate</MenuItem>
                <MenuItem value="after_checkout">After Checkout</MenuItem>
                <MenuItem value="after_inspection">After Inspection</MenuItem>
                <MenuItem value="custom">Custom Terms</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateDeposit} 
            variant="contained"
            disabled={!depositForm.guest || !depositForm.amount}
          >
            Create Deposit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog} onClose={() => setRefundDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          {selectedDeposit && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Alert severity="info">
                Refunding deposit {selectedDeposit.depositId} - 
                Remaining balance: ${selectedDeposit.amount.remaining.toFixed(2)}
              </Alert>
              
              <TextField
                label="Refund Amount"
                type="number"
                value={refundForm.amount}
                onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText={`Maximum: $${selectedDeposit.amount.remaining.toFixed(2)}`}
              />
              
              <TextField
                label="Reason"
                value={refundForm.reason}
                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                fullWidth
                required
                multiline
                rows={2}
              />
              
              <FormControl fullWidth>
                <InputLabel>Refund Method</InputLabel>
                <Select
                  value={refundForm.method}
                  onChange={(e) => setRefundForm({ ...refundForm, method: e.target.value })}
                  label="Refund Method"
                >
                  <MenuItem value="original_method">Original Payment Method</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="credit">Credit to Account</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleRefundDeposit} 
            variant="contained"
            color="success"
            disabled={!refundForm.amount || !refundForm.reason}
          >
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deduction Dialog */}
      <Dialog open={deductionDialog} onClose={() => setDeductionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Deduction</DialogTitle>
        <DialogContent>
          {selectedDeposit && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Alert severity="warning">
                Adding deduction to deposit {selectedDeposit.depositId} - 
                Remaining balance: ${selectedDeposit.amount.remaining.toFixed(2)}
              </Alert>
              
              <TextField
                label="Deduction Amount"
                type="number"
                value={deductionForm.amount}
                onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                helperText={`Maximum: $${selectedDeposit.amount.remaining.toFixed(2)}`}
              />
              
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={deductionForm.category}
                  onChange={(e) => setDeductionForm({ ...deductionForm, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="damage">Damage</MenuItem>
                  <MenuItem value="cleaning">Cleaning</MenuItem>
                  <MenuItem value="lost_item">Lost Item</MenuItem>
                  <MenuItem value="violation">Policy Violation</MenuItem>
                  <MenuItem value="fee">Additional Fee</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Reason"
                value={deductionForm.reason}
                onChange={(e) => setDeductionForm({ ...deductionForm, reason: e.target.value })}
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={deductionForm.description}
                onChange={(e) => setDeductionForm({ ...deductionForm, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
              
              <Button
                variant="outlined"
                startIcon={<PhotoIcon />}
                fullWidth
              >
                Add Evidence Photos
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeductionDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddDeduction} 
            variant="contained"
            color="warning"
            disabled={!deductionForm.amount || !deductionForm.reason}
          >
            Add Deduction
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DepositsPage;